import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import MenuItem from '@mui/material/MenuItem';
import MenuList from '@mui/material/MenuList';
import { SiteInfo, FeatureFlag } from '@cbo/shared-library';
import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useSites } from '../../contexts/siteContext';
import { organizationViewSite } from './SiteSelector';
import { allSiteRouteWithRoles } from '../utils/siteUtils';
import { useFeatureFlag } from '../../utils/hooks/useFeatureFlag';
import isPermitted from '../../lib/permissions';
import useCurrentSite from '../../utils/hooks/useCurrentSite';
import { useUsers } from '../../contexts/userContext';

type AllSitesListProps = {
  searchValue: string;
  handleSiteSelection: (site: SiteInfo) => void;
};

function AllSitesList(props: AllSitesListProps) {
  const { searchValue, handleSiteSelection } = props;
  const { t } = useTranslation();
  const location = useLocation();
  const user = useUsers();
  const isEuLevelRolesEnabled = useFeatureFlag(FeatureFlag.euLevelRoles);
  const { isSingleSiteOrg } = useCurrentSite();
  const { selectedSite, sortedSites } = useSites();

  const filteredSites = useMemo(() => {
    const allSiteRoute = allSiteRouteWithRoles.find((route) => location?.pathname?.includes(route.basePath));
    const hasOrgPermission = isEuLevelRolesEnabled ? allSiteRoute?.newRole : allSiteRoute?.oldRole;
    const hasPermission =
      allSiteRoute && hasOrgPermission && isPermitted(user, hasOrgPermission, false) && !isSingleSiteOrg;

    const sites = hasPermission ? [organizationViewSite, ...sortedSites] : sortedSites;
    return sites.filter(
      (site: SiteInfo) =>
        site.referenceId?.toLowerCase().includes(searchValue.toLowerCase()) ||
        site.name?.toLowerCase().includes(searchValue.toLowerCase())
    );
  }, [location.pathname, searchValue, sortedSites, isEuLevelRolesEnabled, isSingleSiteOrg, user]);

  if (filteredSites.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', height: '100px', alignItems: 'center' }}>
        <Typography variant='body1'>{t('multiselect.noResults')}</Typography>
      </Box>
    );
  }

  return (
    <MenuList sx={{ padding: 0 }}>
      {filteredSites.map((site) => (
        <MenuItem
          key={site.enterpriseUnitId}
          onClick={() => handleSiteSelection(site)}
          selected={site.enterpriseUnitId === selectedSite?.enterpriseUnitId}
          sx={{ height: '48px', margin: '4px 0px', borderRadius: '8px' }}
        >
          <Typography variant='body1'>{site.name}</Typography>
        </MenuItem>
      ))}
    </MenuList>
  );
}

export default AllSitesList;
